import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PlusIcon, PencilSquareIcon, TrashIcon, CheckCircleIcon, ExclamationCircleIcon, } from '@heroicons/react/24/outline';
import { apiClient } from '@/lib/api';
import { Button } from '@/components/Button';
import { Card, CardHeader } from '@/components/Card';
import { Pill } from '@/components/Pill';
const providersApi = {
    list: () => apiClient.get('/sslwidewatch/alerts/providers'),
    create: (b) => apiClient.post('/sslwidewatch/alerts/providers', b),
    update: (id, b) => apiClient.patch(`/sslwidewatch/alerts/providers/${id}`, b),
    remove: (id) => apiClient.delete(`/sslwidewatch/alerts/providers/${id}`),
    test: (id, recipient) => apiClient.post(`/sslwidewatch/alerts/providers/${id}/test`, { recipient }),
};
const KINDS = [
    {
        value: 'SMTP',
        label: 'SMTP',
        channel: 'EMAIL',
        fields: [
            { key: 'host', label: 'Host', required: true, hint: 'e.g. smtp.example.com' },
            {
                key: 'port',
                label: 'Port',
                type: 'select',
                required: true,
                valueType: 'number',
                hint: '587 = STARTTLS (recommended). 465 = TLS-on-connect. 25 = legacy plaintext.',
                options: [
                    { value: '587', label: '587 — STARTTLS (recommended)' },
                    { value: '465', label: '465 — TLS-on-connect' },
                    { value: '25', label: '25 — plaintext (legacy)' },
                    { value: '2525', label: '2525 — STARTTLS (alternative)' },
                ],
            },
            { key: 'username', label: 'Username', required: true },
            { key: 'password', label: 'Password', type: 'password', required: true },
            { key: 'fromAddress', label: 'From address', required: true },
            { key: 'fromName', label: 'From name (optional)', hint: 'Display name shown in the recipient inbox' },
            {
                key: 'tls',
                label: 'TLS mode',
                type: 'select',
                required: true,
                hint: 'STARTTLS pairs with port 587. SMTPS pairs with port 465. NONE only for trusted relays.',
                options: [
                    { value: 'STARTTLS', label: 'STARTTLS (recommended)' },
                    { value: 'SMTPS', label: 'SMTPS (TLS on connect)' },
                    { value: 'NONE', label: 'None (plaintext)' },
                ],
            },
        ],
    },
    {
        value: 'ZEPTOMAIL',
        label: 'ZeptoMail (Zoho)',
        channel: 'EMAIL',
        fields: [
            { key: 'token', label: 'Send-mail token', required: true, hint: 'From the ZeptoMail console → Mail Agents (include the "Zoho-enczapikey " prefix)' },
            { key: 'fromAddress', label: 'From address', required: true, hint: 'Must be a verified sender on the ZeptoMail Mail Agent' },
            { key: 'bounceAddress', label: 'Bounce address (optional)', hint: 'Defaults to the platform bounce address if blank' },
        ],
    },
    {
        value: 'POSTMARK',
        label: 'Postmark',
        channel: 'EMAIL',
        fields: [
            { key: 'serverToken', label: 'Server API token', type: 'password', required: true, hint: 'Postmark → Servers → API Tokens' },
            { key: 'fromAddress', label: 'From address', required: true, hint: 'Must be a verified Sender Signature' },
            { key: 'messageStream', label: 'Message stream (optional)', hint: 'Defaults to "outbound"' },
        ],
    },
    {
        value: 'SENDGRID',
        label: 'SendGrid',
        channel: 'EMAIL',
        fields: [
            { key: 'apiKey', label: 'API key', type: 'password', required: true, hint: 'SendGrid → Settings → API Keys (Mail Send scope)' },
            { key: 'fromAddress', label: 'From address', required: true, hint: 'Must be a verified sender' },
        ],
    },
    {
        value: 'MAILGUN',
        label: 'Mailgun',
        channel: 'EMAIL',
        fields: [
            { key: 'apiKey', label: 'API key', type: 'password', required: true },
            { key: 'domain', label: 'Sending domain', required: true, hint: 'e.g. mg.your-company.com' },
            { key: 'fromAddress', label: 'From address', required: true },
            {
                key: 'region', label: 'Region', type: 'select',
                hint: 'EU only if your Mailgun account is EU-hosted.',
                options: [
                    { value: 'us', label: 'US (default)' },
                    { value: 'eu', label: 'EU' },
                ],
            },
        ],
    },
    {
        value: 'TWILIO',
        label: 'Twilio (SMS)',
        channel: 'SMS',
        fields: [
            { key: 'accountSid', label: 'Account SID', required: true },
            { key: 'authToken', label: 'Auth Token', type: 'password', required: true },
            { key: 'fromNumber', label: 'From number (E.164)', required: true, hint: '+15551234567 — leave blank if using a Messaging Service SID' },
            { key: 'messagingServiceSid', label: 'Messaging Service SID (optional)', hint: 'Use this OR a from number, not both' },
        ],
    },
    {
        value: 'VONAGE',
        label: 'Vonage (SMS)',
        channel: 'SMS',
        fields: [
            { key: 'senderNumber', label: 'Sender number (E.164)', required: true, hint: '+15551234567' },
        ],
    },
    {
        value: 'SLACK_WEBHOOK',
        label: 'Slack incoming webhook',
        channel: 'CHAT',
        fields: [
            { key: 'webhookUrl', label: 'Webhook URL', required: true, hint: 'From a Slack app → Incoming Webhooks' },
            { key: 'channel', label: 'Channel override (optional)', hint: 'e.g. #ops-alerts. Overrides the webhook’s default channel.' },
        ],
    },
    {
        value: 'DISCORD_WEBHOOK',
        label: 'Discord webhook',
        channel: 'CHAT',
        fields: [
            { key: 'webhookUrl', label: 'Webhook URL', required: true, hint: 'Discord → Channel → Integrations → Webhooks' },
        ],
    },
    {
        value: 'MS_TEAMS_WEBHOOK',
        label: 'Microsoft Teams webhook',
        channel: 'CHAT',
        fields: [
            { key: 'webhookUrl', label: 'Webhook URL', required: true, hint: 'Teams channel → Connectors → Incoming Webhook' },
        ],
    },
    {
        value: 'GENERIC_WEBHOOK',
        label: 'Generic webhook',
        channel: 'WEBHOOK',
        fields: [
            { key: 'url', label: 'URL', required: true, hint: 'Must be https://' },
            { key: 'method', label: 'Method (default POST)' },
            { key: 'hmacSecret', label: 'HMAC secret (optional)', type: 'password', hint: 'If set, we sign the body with HMAC-SHA256 in X-WideWatch-Signature' },
        ],
    },
];
export function AlertProvidersPage() {
    const qc = useQueryClient();
    const [editing, setEditing] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const { data: providers = [], isLoading, isError, error } = useQuery({
        queryKey: ['alerts', 'providers'],
        queryFn: providersApi.list,
    });
    const removeMut = useMutation({
        mutationFn: (id) => providersApi.remove(id),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['alerts', 'providers'] }),
    });
    const setDefaultMut = useMutation({
        mutationFn: (p) => providersApi.update(p.id, { isDefaultForChannel: true }),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['alerts', 'providers'] }),
    });
    const toggleEnabledMut = useMutation({
        mutationFn: (p) => providersApi.update(p.id, { enabled: !p.enabled }),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['alerts', 'providers'] }),
    });
    const testMut = useMutation({
        mutationFn: ({ id, recipient }) => providersApi.test(id, recipient),
        onSuccess: (res) => {
            qc.invalidateQueries({ queryKey: ['alerts', 'providers'] });
            alert(formatTestResult(res));
        },
        onError: (e) => {
            alert('✗ Test send failed\n\n' + (e instanceof Error ? e.message : String(e)));
        },
    });
    function startTest(p) {
        const placeholder = p.channel === 'EMAIL' ? 'oncall@your-company.com'
            : p.channel === 'SMS' ? '+15551234567'
                : p.channel === 'CHAT' || p.channel === 'WEBHOOK' ? '(uses provider config URL — recipient ignored)'
                    : 'recipient';
        const recipient = prompt(`Send a test ${p.channel} alert via "${p.displayName}".\n\nRecipient (${p.channel}):`, placeholder.startsWith('(') ? '' : placeholder);
        if (recipient && recipient.trim()) {
            testMut.mutate({ id: p.id, recipient: recipient.trim() });
        }
    }
    return (_jsxs("div", { className: "space-y-6", children: [_jsxs("header", { className: "flex flex-col gap-4 md:flex-row md:items-end md:justify-between", children: [_jsxs("div", { children: [_jsx("h1", { className: "text-3xl font-semibold tracking-tight", children: "Alert providers" }), _jsx("p", { className: "mt-1 text-sm text-[var(--ink-500)]", children: "Where alerts get delivered. Configure your own SMTP, Twilio, Slack, etc. \u2014 the alerting engine routes through your default for each channel." })] }), _jsx(Button, { variant: "primary", leftIcon: _jsx(PlusIcon, { className: "h-4 w-4" }), onClick: () => {
                            setEditing(null);
                            setShowForm(true);
                        }, children: "Add provider" })] }), showForm && (_jsx(ProviderForm, { initial: editing, onCancel: () => {
                    setShowForm(false);
                    setEditing(null);
                }, onSaved: () => {
                    setShowForm(false);
                    setEditing(null);
                    qc.invalidateQueries({ queryKey: ['alerts', 'providers'] });
                } })), _jsxs(Card, { children: [_jsx(CardHeader, { title: _jsxs("span", { children: ["All ", _jsx("em", { className: "italic", children: "providers" })] }), subtitle: `${providers.length} configured` }), isLoading ? (_jsx("div", { className: "px-5 py-6 text-sm text-[var(--ink-500)]", children: "Loading\u2026" })) : isError ? (_jsx("div", { className: "px-5 py-6 text-sm text-[var(--crimson)]", children: error instanceof Error ? error.message : 'Request failed' })) : providers.length === 0 ? (_jsx("div", { className: "px-5 py-10 text-center text-sm text-[var(--ink-500)]", children: "No providers yet. Add one to start receiving alerts via email, SMS, or chat." })) : (_jsx("div", { className: "overflow-x-auto", children: _jsxs("table", { className: "w-full text-sm", children: [_jsx("thead", { children: _jsxs("tr", { className: "border-b border-[var(--line)] text-left text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--ink-500)]", children: [_jsx("th", { className: "px-5 py-2.5", children: "Display name" }), _jsx("th", { className: "px-5 py-2.5", children: "Channel" }), _jsx("th", { className: "px-5 py-2.5", children: "Provider" }), _jsx("th", { className: "px-5 py-2.5", children: "Default" }), _jsx("th", { className: "px-5 py-2.5", children: "Status" }), _jsx("th", { className: "px-5 py-2.5", children: "Last verified" }), _jsx("th", { className: "px-5 py-2.5 text-right", children: "Actions" })] }) }), _jsx("tbody", { children: providers.map((p) => (_jsxs("tr", { className: "border-b border-[var(--line)] last:border-b-0", children: [_jsx("td", { className: "px-5 py-3 font-medium text-[var(--ink-900)]", children: p.displayName }), _jsx("td", { className: "px-5 py-3 text-[var(--ink-500)]", children: p.channel }), _jsx("td", { className: "px-5 py-3 text-[var(--ink-500)]", children: p.provider }), _jsx("td", { className: "px-5 py-3", children: p.defaultForChannel ? (_jsx(Pill, { tone: "green", children: "Default" })) : (_jsx("button", { type: "button", className: "text-[12px] text-[var(--ink-500)] underline hover:text-[var(--royal)]", onClick: () => setDefaultMut.mutate(p), disabled: setDefaultMut.isPending, children: "Make default" })) }), _jsx("td", { className: "px-5 py-3", children: _jsx("button", { type: "button", onClick: () => toggleEnabledMut.mutate(p), disabled: toggleEnabledMut.isPending, className: "cursor-pointer", "aria-label": p.enabled ? 'Disable' : 'Enable', children: p.enabled ? _jsx(Pill, { tone: "green", children: "Enabled" }) : _jsx(Pill, { tone: "neutral", children: "Disabled" }) }) }), _jsx("td", { className: "px-5 py-3 text-[var(--ink-500)]", children: p.lastVerifiedStatus === 'NEVER' ? (_jsx("span", { children: "Never" })) : p.lastVerifiedStatus === 'OK' ? (_jsxs("span", { className: "inline-flex items-center gap-1 text-[var(--emerald-text)]", children: [_jsx(CheckCircleIcon, { className: "h-3.5 w-3.5" }), " OK"] })) : (_jsxs("span", { className: "inline-flex items-center gap-1 text-[var(--crimson)]", title: p.lastErrorMessage ?? undefined, children: [_jsx(ExclamationCircleIcon, { className: "h-3.5 w-3.5" }), " Failed"] })) }), _jsx("td", { className: "px-5 py-3", children: _jsxs("div", { className: "flex justify-end gap-1", children: [_jsx(Button, { variant: "ghost", size: "sm", onClick: () => startTest(p), disabled: testMut.isPending, children: "Test" }), _jsx(Button, { variant: "ghost", size: "sm", "aria-label": "Edit", onClick: () => {
                                                                setEditing(p);
                                                                setShowForm(true);
                                                            }, children: _jsx(PencilSquareIcon, { className: "h-4 w-4" }) }), _jsx(Button, { variant: "ghost", size: "sm", "aria-label": "Delete", onClick: () => {
                                                                if (confirm(`Delete provider "${p.displayName}"?`)) {
                                                                    removeMut.mutate(p.id);
                                                                }
                                                            }, disabled: removeMut.isPending, children: _jsx(TrashIcon, { className: "h-4 w-4" }) })] }) })] }, p.id))) })] }) }))] })] }));
}
function ProviderForm({ initial, onCancel, onSaved, }) {
    const qc = useQueryClient();
    // Map back from existing provider record to a kind so editing pre-fills.
    const initialKind = KINDS.find((k) => k.value === initial?.provider)?.value
        ?? 'SMTP';
    const [kind, setKind] = useState(initialKind);
    const [displayName, setDisplayName] = useState(initial?.displayName ?? '');
    const [setAsDefault, setSetAsDefault] = useState(initial?.defaultForChannel ?? true);
    const [enabled, setEnabled] = useState(initial?.enabled ?? true);
    // Initialize the per-field state from initial.configJson (if editing).
    const initialConfig = (() => {
        if (!initial)
            return {};
        try {
            const parsed = JSON.parse(initial.configJson);
            return typeof parsed === 'object' && parsed !== null ? parsed : {};
        }
        catch {
            return {};
        }
    })();
    const [fields, setFields] = useState(initialConfig);
    const [error, setError] = useState(null);
    const def = KINDS.find((k) => k.value === kind);
    /**
     * Build the config JSON blob the backend stores. We strip empty optional
     * fields (so the adapter sees absent keys, not empty strings) and coerce
     * any field with `valueType: 'number'` to a real Number — required for
     * SMTP port, which the adapter rejects unless it's `instanceof Number`.
     */
    function buildConfigJson() {
        const out = {};
        for (const f of def.fields) {
            const raw = fields[f.key];
            if (raw === undefined || raw === null || raw === '')
                continue;
            if (f.valueType === 'number' && typeof raw === 'string') {
                const n = Number(raw);
                if (Number.isFinite(n))
                    out[f.key] = n;
                continue;
            }
            out[f.key] = raw;
        }
        return JSON.stringify(out);
    }
    const saveMut = useMutation({
        mutationFn: (b) => initial ? providersApi.update(initial.id, b) : providersApi.create(b),
        onSuccess: onSaved,
        onError: (e) => setError(e instanceof Error ? e.message : 'Save failed'),
    });
    // Inline test from inside the form. For an existing provider this
    // tests the SAVED config (so unsaved tweaks aren't honoured — save
    // first if you've changed something). For a new provider, save first
    // then test, so the customer doesn't have to click twice.
    const testFromFormMut = useMutation({
        mutationFn: async () => {
            // Validate first
            for (const f of def.fields) {
                if (f.required && !fields[f.key]) {
                    throw new Error(`${f.label} is required.`);
                }
            }
            const recipient = prompt(`Send a test ${def.channel} alert via "${displayName.trim() || def.label}".\n\nRecipient (${def.channel}):`, def.channel === 'EMAIL' ? 'oncall@your-company.com'
                : def.channel === 'SMS' ? '+15551234567'
                    : '');
            if (!recipient || !recipient.trim()) {
                throw new Error('Cancelled');
            }
            // Save (or update) the provider first so the dispatcher can find
            // the latest config row, then ask it to send.
            let savedId = initial?.id;
            const body = {
                channel: def.channel,
                provider: kind,
                displayName: displayName.trim() || def.label,
                configJson: buildConfigJson(),
                enabled,
                isDefaultForChannel: setAsDefault,
            };
            if (savedId) {
                await providersApi.update(savedId, body);
            }
            else {
                const created = await providersApi.create(body);
                savedId = created.id;
            }
            return providersApi.test(savedId, recipient.trim());
        },
        onSuccess: (res) => {
            qc.invalidateQueries({ queryKey: ['alerts', 'providers'] });
            alert(formatTestResult(res));
        },
        onError: (e) => {
            const msg = e instanceof Error ? e.message : String(e);
            if (msg !== 'Cancelled') {
                setError(msg);
            }
        },
    });
    const onSubmit = (e) => {
        e.preventDefault();
        setError(null);
        if (!displayName.trim()) {
            setError('Display name is required.');
            return;
        }
        // Validate required fields per kind
        for (const f of def.fields) {
            if (f.required && !fields[f.key]) {
                setError(`${f.label} is required.`);
                return;
            }
        }
        saveMut.mutate({
            channel: def.channel,
            provider: kind,
            displayName: displayName.trim(),
            configJson: buildConfigJson(),
            enabled,
            isDefaultForChannel: setAsDefault,
        });
    };
    return (_jsxs(Card, { children: [_jsx(CardHeader, { title: initial ? 'Edit provider' : 'New provider' }), _jsxs("form", { onSubmit: onSubmit, className: "space-y-4 px-5 py-5", children: [_jsx(Field, { label: "Provider type", required: true, children: _jsx("select", { value: kind, onChange: (e) => {
                                setKind(e.target.value);
                                setFields({});
                            }, className: inputCls, disabled: initial != null, children: KINDS.map((k) => (_jsxs("option", { value: k.value, children: [k.label, " (", k.channel, ")"] }, k.value))) }) }), _jsx(Field, { label: "Display name", required: true, hint: "Customer-facing label, e.g. 'Production SMTP'", children: _jsx("input", { type: "text", value: displayName, onChange: (e) => setDisplayName(e.target.value), required: true, className: inputCls }) }), _jsxs("div", { className: "rounded-[var(--r-md)] border border-[var(--line)] bg-[var(--surface-glass-soft)] px-4 py-4 space-y-4", children: [_jsxs("div", { className: "text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--ink-500)]", style: { fontFamily: 'var(--f-mono)' }, children: [def.label, " \u2014 config"] }), def.fields.map((f) => (_jsx(Field, { label: f.label, hint: f.hint, required: f.required, children: f.type === 'checkbox' ? (_jsxs("label", { className: "flex items-center gap-2 text-sm", children: [_jsx("input", { type: "checkbox", checked: Boolean(fields[f.key]), onChange: (e) => setFields({ ...fields, [f.key]: e.target.checked }) }), _jsx("span", { children: "Yes" })] })) : f.type === 'select' && f.options ? (_jsxs("select", { value: fields[f.key] ?? '', onChange: (e) => setFields({ ...fields, [f.key]: e.target.value }), className: inputCls, required: f.required, children: [_jsx("option", { value: "", children: "Select\u2026" }), f.options.map((o) => (_jsx("option", { value: o.value, children: o.label }, o.value)))] })) : (_jsx("input", { type: f.type ?? 'text', value: fields[f.key] ?? '', onChange: (e) => setFields({ ...fields, [f.key]: e.target.value }), className: inputCls, required: f.required })) }, f.key)))] }), _jsxs("div", { className: "flex flex-col gap-2 text-sm", children: [_jsxs("label", { className: "flex items-center gap-2", children: [_jsx("input", { type: "checkbox", checked: setAsDefault, onChange: (e) => setSetAsDefault(e.target.checked) }), _jsxs("span", { children: ["Use as the default for the ", def.channel, " channel"] })] }), _jsxs("label", { className: "flex items-center gap-2", children: [_jsx("input", { type: "checkbox", checked: enabled, onChange: (e) => setEnabled(e.target.checked) }), _jsx("span", { children: "Enabled" })] })] }), error && (_jsx("div", { role: "alert", className: "rounded-[var(--r-md)] border border-[var(--rose-200)] bg-[var(--rose-50)] px-3 py-2 text-[13px] text-[var(--crimson)]", children: error })), _jsxs("div", { className: "flex flex-wrap justify-end gap-2", children: [_jsx(Button, { variant: "ghost", type: "button", onClick: onCancel, children: "Cancel" }), _jsx(Button, { variant: "ghost", type: "button", onClick: () => testFromFormMut.mutate(), disabled: testFromFormMut.isPending || saveMut.isPending, title: "Save with current values, then send a test alert", children: testFromFormMut.isPending ? 'Testing…' : 'Save & test' }), _jsx(Button, { type: "submit", variant: "primary", disabled: saveMut.isPending || testFromFormMut.isPending, children: saveMut.isPending ? 'Saving…' : initial ? 'Save' : 'Add provider' })] })] })] }));
}
/**
 * Pretty-print the dispatcher's test response. Provider error bodies
 * are often a wall of JSON (ZeptoMail in particular returns a nested
 * {error:{details:[...]}} blob). We try to extract the most useful
 * human-readable bit; fall back to truncating raw text so the alert()
 * doesn't take over the whole screen.
 */
function formatTestResult(res) {
    const head = (res.ok ? '✓ Test sent' : '✗ Test failed')
        + '\n\nRecipient: ' + res.recipient
        + '\nStatus: ' + res.status;
    if (!res.message)
        return head;
    // Try to parse the message as JSON — provider error responses usually
    // come through verbatim from the upstream API.
    let pretty = res.message;
    try {
        const parsed = JSON.parse(res.message);
        pretty = humaniseProviderError(parsed) ?? truncate(res.message, 400);
    }
    catch {
        pretty = truncate(res.message, 400);
    }
    return head + '\n\n' + pretty;
}
/**
 * Walk a parsed provider error blob and pull out the most actionable
 * piece. Recognises ZeptoMail's `{error:{details:[{message,target_value}]}}`
 * shape; falls back to top-level `message` / `error` / stringification.
 */
function humaniseProviderError(parsed) {
    if (parsed === null || typeof parsed !== 'object')
        return null;
    const obj = parsed;
    // ZeptoMail-style nested error.
    const errBlock = obj.error;
    if (errBlock && typeof errBlock === 'object') {
        const e = errBlock;
        const details = e.details;
        if (Array.isArray(details) && details.length > 0) {
            const first = details[0];
            const msg = typeof first.message === 'string' ? first.message : null;
            const target = typeof first.target === 'string' ? first.target : null;
            const tval = typeof first.target_value === 'string' ? first.target_value : null;
            if (msg) {
                let out = msg;
                if (target && tval)
                    out += ` (${target}: ${tval})`;
                else if (tval)
                    out += ` (${tval})`;
                return out;
            }
        }
        if (typeof e.message === 'string')
            return e.message;
    }
    // Generic { message: "..." } shape.
    if (typeof obj.message === 'string')
        return obj.message;
    return null;
}
function truncate(s, max) {
    return s.length > max ? s.slice(0, max) + '…' : s;
}
const inputCls = 'w-full rounded-[var(--r-md)] border border-[var(--line)] bg-white px-3.5 py-2.5 text-[14px] text-[var(--ink-900)] focus:border-[var(--lav-200)] focus:outline-none focus:ring-2 focus:ring-[var(--lav-100)]';
function Field({ label, hint, required, children, }) {
    return (_jsxs("label", { className: "block space-y-1.5", children: [_jsxs("span", { className: "text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--ink-500)]", style: { fontFamily: 'var(--f-mono)' }, children: [label, required && _jsx("span", { className: "ml-1 text-[var(--crimson)]", children: "*" })] }), children, hint && (_jsx("span", { className: "block text-[12px] text-[var(--ink-500)]", children: hint }))] }));
}
